/**
 * Billing Resource
 *
 * Thin 1:1 mapping to the billing API. No business logic, no default
 * parameter injection. The SDK passes params as-is.
 */

import type { HttpLayer } from '../http.js';
import type {
  Plan,
  Subscription,
  CreditBalance,
  CreditTransactionsResponse,
  Entitlements,
  ChangePlanRequest,
  AssignPlanRequest,
  PlanAssignment,
  PlanWhatIfParams,
  AdminAccountParams,
  AdminAdjustCreditsRequest,
  AdminOverrideSubscriptionRequest,
  AdminCompAccountRequest,
  AdminListPlansParams,
  AdminListCreditTransactionsParams,
  OnboardingStatus,
  SetupIntentResponse,
  CompleteOnboardingPaymentRequest,
  CompleteOnboardingResponse,
  CurrentPeriodPosition,
  PeriodSummary,
  ListPeriodsParams,
  RetryAdvanceChargeResponse,
  CancelSubscriptionRequest,
  CancelSubscriptionResponse,
  ReactivateSubscriptionResponse,
  AdminCancelSubscriptionRequest,
} from '../types/index.js';

export class BillingResource {
  constructor(private http: HttpLayer) {}

  private prefix = '/v1/billing';

  // --- Plans ---

  async listPlans(): Promise<Plan[]> {
    return this.http.get(`${this.prefix}/plans`);
  }

  // --- Subscription ---

  async getSubscription(accountId: string): Promise<Subscription> {
    return this.http.get(`${this.prefix}/${accountId}/subscription`);
  }

  async changePlan(
    accountId: string,
    params: ChangePlanRequest,
  ): Promise<Subscription> {
    return this.http.post(
      `${this.prefix}/${accountId}/subscriptions/change-plan`,
      params,
    );
  }

  // --- Plan Assignments ---

  async assignPlan(
    accountId: string,
    params: AssignPlanRequest,
  ): Promise<PlanAssignment> {
    return this.http.post(
      `${this.prefix}/${accountId}/plans/assign`,
      params,
    );
  }

  async listPlanAssignments(accountId: string): Promise<PlanAssignment[]> {
    return this.http.get(`${this.prefix}/${accountId}/plans/assignments`);
  }

  async activatePlanAssignment(
    accountId: string,
    assignmentId: string,
  ): Promise<PlanAssignment> {
    return this.http.post(
      `${this.prefix}/${accountId}/plans/assignments/${assignmentId}/activate`,
    );
  }

  // --- Plan What-If ---

  async planWhatIf(
    accountId: string,
    planId: string,
    params: PlanWhatIfParams,
  ): Promise<unknown> {
    return this.http.get(
      `${this.prefix}/${accountId}/plans/${planId}/what-if`,
      params,
    );
  }

  // --- Credits ---

  async getCreditBalance(accountId: string): Promise<CreditBalance> {
    return this.http.get(`${this.prefix}/${accountId}/credits/balance`);
  }

  async getCreditTransactions(
    accountId: string,
    params?: { limit?: number },
  ): Promise<CreditTransactionsResponse> {
    return this.http.get(
      `${this.prefix}/${accountId}/credits/transactions`,
      params,
    );
  }

  // --- Entitlements ---

  async getEntitlements(accountId: string): Promise<Entitlements> {
    return this.http.get(`${this.prefix}/${accountId}/entitlements`);
  }

  // --- Periods ---

  /**
   * Get current billing period position.
   *
   * Returns the account's position for the open (or awaiting-payment) period,
   * including allowance, consumed credits, overage projection, and daily usage.
   *
   * @param accountId - The account identifier
   * @returns Current period position with daily consumption breakdown
   */
  async getCurrentPeriod(accountId: string): Promise<CurrentPeriodPosition> {
    return this.http.get(`${this.prefix}/${accountId}/current-period`);
  }

  /**
   * List billing periods.
   *
   * Returns settled and closed periods, newest first. The per-period breakdown
   * includes advance amount, included credits, credits consumed, and overage.
   *
   * @param accountId - The account identifier
   * @param params - Optional pagination params (limit)
   * @returns Array of period summaries
   */
  async listPeriods(
    accountId: string,
    params?: ListPeriodsParams,
  ): Promise<PeriodSummary[]> {
    return this.http.get(`${this.prefix}/${accountId}/periods`, params);
  }

  /**
   * Retry a declined advance charge.
   *
   * Signals the period-open workflow to re-attempt the declined advance charge.
   * Only valid when the period is in awaiting_payment status. This method
   * does not charge directly — it signals the workflow to retry.
   *
   * @param accountId - The account identifier
   * @returns Response indicating whether the retry was accepted
   */
  async retryAdvanceCharge(
    accountId: string,
  ): Promise<RetryAdvanceChargeResponse> {
    return this.http.post(`${this.prefix}/${accountId}/advance-charge/retry`);
  }

  /**
   * Request cancellation of the account's subscription.
   *
   * Initiates a deferred cancellation that takes effect at the end of the
   * current billing period. The subscription remains active until then.
   *
   * @param accountId - The account identifier
   * @param request - Optional cancellation details (reason)
   * @returns The cancellation state
   */
  async cancelSubscription(
    accountId: string,
    request?: CancelSubscriptionRequest,
  ): Promise<CancelSubscriptionResponse> {
    return this.http.post(
      `${this.prefix}/${accountId}/subscription/cancel`,
      request ?? {},
    );
  }

  /**
   * Reactivate a pending subscription cancellation.
   *
   * Withdraws a pending cancellation before its effective date. Not allowed
   * after the cancellation has already taken effect.
   *
   * @param accountId - The account identifier
   * @returns The updated cancellation state
   */
  async reactivateSubscription(
    accountId: string,
  ): Promise<ReactivateSubscriptionResponse> {
    return this.http.post(`${this.prefix}/${accountId}/subscription/reactivate`);
  }

  // --- Admin ---

  async adminAdjustCredits(
    params: AdminAdjustCreditsRequest,
  ): Promise<unknown> {
    return this.http.post(`${this.prefix}/admin/adjust-credits`, params);
  }

  async adminOverrideSubscription(
    params: AdminOverrideSubscriptionRequest,
  ): Promise<Subscription> {
    return this.http.post(
      `${this.prefix}/admin/override-subscription`,
      params,
    );
  }

  async adminCompAccount(
    params: AdminCompAccountRequest,
  ): Promise<unknown> {
    return this.http.post(`${this.prefix}/admin/comp-account`, params);
  }

  async adminGetSubscription(
    params: AdminAccountParams,
  ): Promise<Subscription> {
    return this.http.get(
      `${this.prefix}/admin/account/subscription`,
      params,
    );
  }

  async adminGetCreditBalance(
    params: AdminAccountParams,
  ): Promise<CreditBalance> {
    return this.http.get(
      `${this.prefix}/admin/account/credits/balance`,
      params,
    );
  }

  async adminListCreditTransactions(
    params: AdminListCreditTransactionsParams,
  ): Promise<CreditTransactionsResponse> {
    return this.http.get(
      `${this.prefix}/admin/account/credits/transactions`,
      params,
    );
  }

  async adminListPlans(
    params?: AdminListPlansParams,
  ): Promise<Plan[]> {
    return this.http.get(`${this.prefix}/admin/plans`, params);
  }

  /**
   * Platform admin cancellation of a subscription.
   *
   * Supports both deferred and immediate modes. Immediate cancellation
   * takes effect immediately, while deferred cancellation takes effect
   * at the end of the current billing period.
   *
   * @param params - Admin cancellation request with account, organization, mode, and optional reason
   * @returns The cancellation state
   */
  async adminCancelSubscription(
    params: AdminCancelSubscriptionRequest,
  ): Promise<CancelSubscriptionResponse> {
    return this.http.post(`${this.prefix}/admin/cancel-subscription`, params);
  }

  // --- Onboarding ---

  async getOnboardingStatus(accountId: string): Promise<OnboardingStatus> {
    return this.http.get(`${this.prefix}/${accountId}/onboarding`);
  }

  async getSetupIntent(accountId: string): Promise<SetupIntentResponse> {
    return this.http.post(`${this.prefix}/${accountId}/onboarding/setup-intent`);
  }

  async completeOnboardingPayment(
    accountId: string,
    params: CompleteOnboardingPaymentRequest,
  ): Promise<CompleteOnboardingResponse> {
    return this.http.post(
      `${this.prefix}/${accountId}/onboarding/complete-payment`,
      params,
    );
  }

  async completeOnboardingComplimentary(
    accountId: string,
  ): Promise<CompleteOnboardingResponse> {
    return this.http.post(
      `${this.prefix}/${accountId}/onboarding/complete-complimentary`,
    );
  }
}
